import { useParams, Link } from 'react-router-dom';
import SEO from '@/components/feature/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import VideoCard from '@/components/base/VideoCard';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';
import { getVideoById, categories, formatViews, videos } from '@/mocks/videos';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(id || '');
  const video = getVideoById(id || '');

  const displayItem = article || video;

  if (!displayItem) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center">
              <i className="ri-error-warning-line text-5xl text-foreground-300"></i>
            </div>
            <h1 className="font-heading text-xl text-foreground-900 mb-2">内容未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">该内容可能已被移除或链接无效</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const isArticle = !!article;
  const itemTitle = 'title' in displayItem ? displayItem.title : '';
  const itemSummary = isArticle ? (displayItem as typeof article)!.summary : (displayItem as typeof video)!.description;
  const itemCategory = displayItem.category;
  const itemDate = displayItem.date;
  const itemViews = displayItem.views;
  const coverImg = isArticle ? (displayItem as typeof article)!.coverImage : (displayItem as typeof video)!.thumbnail;
  const tags = isArticle ? (displayItem as typeof article)!.tags : [];
  const author = isArticle ? (displayItem as typeof article)!.author : '蘑菇成品人视频';
  const content = isArticle ? (displayItem as typeof article)!.content : '';
  const videoDuration = !isArticle ? (displayItem as typeof video)!.duration : '';

  const relatedArticles = isArticle ? getRelatedArticles(article!.id, article!.category, 3) : [];
  const categoryObj = categories.find((c) => c.slug === itemCategory);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: categoryObj?.name || '分类', href: `/category/${itemCategory}` },
    { label: itemTitle },
  ];

  return (
    <>
      <SEO
        title={itemTitle}
        description={itemSummary}
        keywords={tags.join(',') || '蘑菇成品人视频,蘑菇种植'}
        canonicalUrl={`https://mushroom-tutorial.com/article/${id}`}
        ogType="article"
        ogImage={coverImg}
        schemaJson={{
          '@context': 'https://schema.org',
          '@type': isArticle ? 'Article' : 'VideoObject',
          ...(isArticle
            ? {
                headline: itemTitle,
                description: itemSummary,
                image: coverImg,
                author: { '@type': 'Person', name: author },
                datePublished: itemDate,
                publisher: { '@type': 'Organization', name: '蘑菇成品人视频精选合集' },
              }
            : {
                name: itemTitle,
                description: itemSummary,
                thumbnailUrl: coverImg,
                duration: videoDuration,
                uploadDate: itemDate,
              }),
        }}
      />
      <Navbar />
      <main className="pt-16">
        <article>
          <div className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <Breadcrumb items={breadcrumbItems} />
            </div>
          </div>

          <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
            <header className="mb-8 animate-fade-in">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Link
                  to={`/category/${itemCategory}`}
                  className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium hover:bg-secondary-200 transition-colors"
                >
                  {categoryObj?.name || itemCategory}
                </Link>
                {tags.map((tag: string) => (
                  <span key={tag} className="text-xs text-foreground-500">
                    #{tag}
                  </span>
                ))}
                {!isArticle && (
                  <span className="flex items-center gap-1 text-xs text-foreground-500">
                    <i className="ri-timer-line"></i>
                    {videoDuration}
                  </span>
                )}
              </div>
              <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 leading-snug mb-4">
                {itemTitle}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-user-line"></i>
                  </div>
                  {author}
                </span>
                <span className="flex items-center gap-1.5">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-calendar-line"></i>
                  </div>
                  {itemDate}
                </span>
                <span className="flex items-center gap-1.5">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-eye-line"></i>
                  </div>
                  {formatViews(itemViews)} 阅读
                </span>
              </div>
            </header>

            <div className="mb-10 rounded-lg overflow-hidden animate-fade-in">
              <img
                src={coverImg}
                alt={itemTitle}
                title={itemTitle}
                className="w-full aspect-[2/1] object-cover"
              />
            </div>

            {isArticle && content ? (
              <div
                className="prose prose-sm md:prose-base max-w-none animate-fade-in-up text-foreground-800"
                dangerouslySetInnerHTML={{
                  __html: content.replace(
                    /<h2>/g,
                    '<h2 class="font-heading text-xl md:text-2xl text-foreground-900 mt-10 mb-4">'
                  ).replace(
                    /<p>/g,
                    '<p class="text-sm md:text-base leading-relaxed text-foreground-700 mb-4">'
                  ),
                }}
              />
            ) : (
              <div className="animate-fade-in-up">
                <div className="bg-background-100 rounded-lg p-8 md:p-12 text-center mb-8">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                    <i className="ri-play-circle-fill text-3xl text-primary-500"></i>
                  </div>
                  <h2 className="font-heading text-lg text-foreground-900 mb-2">蘑菇成品人视频</h2>
                  <p className="text-sm text-foreground-600 max-w-md mx-auto">
                    {itemSummary}
                  </p>
                  <p className="text-xs text-foreground-400 mt-4">
                    视频时长: {videoDuration} &middot; 分类: {categoryObj?.name}
                  </p>
                </div>
                <div className="text-sm text-foreground-700 leading-relaxed space-y-4">
                  <p>这是一期精心制作的蘑菇成品人视频教程。通过本视频，您将学习到{itemCategory === 'planting' ? '蘑菇种植全流程的核心技术' : itemCategory === 'display' ? '蘑菇成品品质评判标准' : itemCategory === 'cultivation' ? '人工培育操作要点' : itemCategory === 'making' ? '蘑菇制作加工方法' : itemCategory === 'harvest' ? '科学采收保鲜技术' : '实用的蘑菇种植知识'}。</p>
                  <p>在蘑菇成品人视频精选合集中，我们持续更新最新的蘑菇种植视频教程，帮助您快速掌握蘑菇从培育到成品的完整技术。建议配合我们的深度文章一起学习，效果更佳。</p>
                </div>
              </div>
            )}

            <footer className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-foreground-500">标签：</span>
                  {tags.length > 0 ? tags.map((tag: string) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs"
                    >
                      {tag}
                    </span>
                  )) : (
                    <span className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs">
                      蘑菇成品人视频
                    </span>
                  )}
                </div>
                <Link
                  to={`/category/${itemCategory}`}
                  className="inline-flex items-center gap-1.5 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors"
                >
                  查看更多蘑菇成品人视频
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </footer>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-12 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-6">
                相关蘑菇成品人视频教程
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((rel, idx) => (
                  <div
                    key={rel.id}
                    className="animate-fade-in-up"
                    style={{ animationDelay: `${idx * 100}ms` }}
                  >
                    <ArticleCard article={rel} />
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {!isArticle && (
          <section className="py-12 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-6">
                同类别蘑菇成品人视频
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {videos
                  .filter((v) => v.category === itemCategory && v.id !== id)
                  .slice(0, 3)
                  .map((v, idx) => (
                    <div
                      key={v.id}
                      className="animate-fade-in-up"
                      style={{ animationDelay: `${idx * 100}ms` }}
                    >
                      <VideoCard video={v} />
                    </div>
                  ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}